import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { Icon, ObjectCard } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import { useSessionStore } from '~/stores/Session';

interface EntityCardProps {
  entityId: string;
}

const getEntityName = (entity?: EntityWithDetails) => {
  if (!entity) return undefined;
  if (entity.personDetails)
    return `${entity.personDetails.firstName || ''} ${entity.personDetails.lastName || ''}`.trim();
  if (entity.businessDetails) return entity.businessDetails.businessName;
  return undefined;
};

const getEntityIcon = (entity?: EntityWithDetails): keyof typeof Icon | undefined => {
  if (!entity) return undefined;
  return entity.type === 'PERSON' ? 'User' : 'Business';
};

const getEntityType = (entity?: EntityWithDetails) => {
  if (!entity) return undefined;
  return entity.type === 'PERSON' ? 'person' : 'business';
};

export const EntityCard: React.FC<EntityCardProps> = ({ entityId }) => {
  const navigate = useNavigate();
  const { currentPlatform } = useSessionStore();

  const {
    data: entity,
    isLoading,
    error,
  } = useQuery<EntityWithDetails>({
    queryKey: [currentPlatform?.id, 'entity', entityId],
    queryFn: async () =>
      EntityRepository.get({
        id: entityId,
      }),
    enabled: !!entityId,
    gcTime: 30000,
    staleTime: 10000,
  });

  const entityName = getEntityName(entity);
  const entityIcon = getEntityIcon(entity);
  const entityType = getEntityType(entity);

  const handleNavigate = () => {
    navigate(`${ROUTE.ENTITIES}/edit/${entityType}/${entityId}`);
  };

  return (
    <ObjectCard
      error={error ? 'Failed to load entity' : undefined}
      icon={entityIcon}
      isLoading={isLoading}
      primaryText={entityName || 'Unnamed Entity'}
      id={{ label: 'ID:', value: entityId }}
      onClick={handleNavigate}
    />
  );
};
